import {
  RentingCost,
  RentingPosition,
  UpdateRentingCostPositionRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { useQpmCalcRentingCostPositionPricePostMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { useEffect, useState } from "react";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  FloatNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";

export type RentingCostComponentProps = {
  calculationId: string;
  rentingCost: RentingCost;
};

export function RentingCostComponent({
  calculationId,
  rentingCost,
}: RentingCostComponentProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/5">{t("Monitor")}</TableHead>
          <TableHead className="w-1/5 text-right">{t("Anzahl")}</TableHead>
          <TableHead className="w-1/5 text-right">
            {t("Mietpreis pro Stück")}
          </TableHead>
          <TableHead className="w-1/5 text-right">{t("Monatsraten")}</TableHead>
          <TableHead className="w-1/5 text-right">{t("Gesamtpreis")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rentingCost &&
          rentingCost.rentingPositionList.positions.map((position) => (
            <RentingPositionRow
              key={`${position.rentingCostId}-${position.positionId}`}
              position={position}
              calculationId={calculationId}
            />
          ))}
      </TableBody>
    </Table>
  );
}

export type RentingPositionRowProps = {
  calculationId: string;
  position: RentingPosition;
};

function RentingPositionRow({
  calculationId,
  position,
}: RentingPositionRowProps) {
  const [amount, setAmount] = useState(position.monitorAmount);
  const [price, setPrice] = useState(position.price);
  const [updatePriceRequest, setUpdatePriceRequest] =
    useState<UpdateRentingCostPositionRequest>({
      calculationId,
      positionId: position.positionId,
      price: position.price,
    });

  const [setRentingPositionPrice, { isLoading, error, reset, isSuccess }] =
    useQpmCalcRentingCostPositionPricePostMutation();

  useEffect(() => {
    // Update amount if the position prop changes
    setAmount(position.monitorAmount);
  }, [position.monitorAmount]);

  useEffect(() => {
    // Update price if the position prop changes
    setPrice(position.price);
  }, [position.price]);

  useDebouncedMutationWithPersistenceStateContextUpdate(
    updatePriceRequest,
    setRentingPositionPrice,
    isLoading,
    error,
    isSuccess,
    reset,
    750,
  );

  return (
    <TableRow>
      <TableCell className="font-medium">{position.monitorName}</TableCell>
      <TableCell className="text-right">{amount}</TableCell>
      <TableCell className="text-right">
        <FloatNumberInput
          className="justify-self-end"
          value={price}
          fractions={2}
          onChange={(val) => {
            setUpdatePriceRequest({
              ...updatePriceRequest,
              price: val,
            });
          }}
          disabled={isLoading}
        />
      </TableCell>
      <TableCell className="text-right">{position.rentingDuration}</TableCell>
      <TableCell className="text-right">
        <Value value={position.totalCost} />
      </TableCell>
    </TableRow>
  );
}
